import { useMemo, useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import MultiSelect from "../../components/common/MultiSelect";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import ActionCheckbox from "../../components/common/ActionCheckbox";
import { useStore } from "../../context/StoreContext";
import useClientPagination from "../../hooks/useClientPagination";
import {
  useGetUpsellCampaignsQuery,
  useCreateUpsellCampaignMutation,
  useUpdateUpsellCampaignMutation,
  useDeleteUpsellCampaignMutation,
  useActivateUpsellCampaignMutation,
  useDeactivateUpsellCampaignMutation,
  useGetItemsQuery,
} from "../../store/api";

const initialFormState = {
  name: "",
  priority: 0,
  isActive: true,
  triggerItemIds: [],
  upsellItemIds: [],
};

const getCampaignId = (campaign) => campaign?.campaignId || campaign?.id;

const getOrderedUpsellItemIds = (campaign) => {
  if (Array.isArray(campaign?.upsellItemIds)) {
    return campaign.upsellItemIds;
  }
  if (!Array.isArray(campaign?.upsellItems)) {
    return [];
  }
  return [...campaign.upsellItems]
    .sort(
      (a, b) =>
        (a?.sortOrder || a?.order || 0) - (b?.sortOrder || b?.order || 0),
    )
    .map(
      (entry) =>
        entry?.itemId || entry?.id || entry?.item?.itemId || entry?.item?.id,
    )
    .filter(Boolean);
};

const getTriggerItemIds = (campaign) => {
  if (Array.isArray(campaign?.triggerItemIds)) {
    return campaign.triggerItemIds;
  }
  if (!Array.isArray(campaign?.triggerItems)) {
    return [];
  }
  return campaign.triggerItems
    .map(
      (entry) =>
        entry?.itemId || entry?.id || entry?.item?.itemId || entry?.item?.id,
    )
    .filter(Boolean);
};

const UpsellCampaigns = () => {
  const { selectedStoreId } = useStore();

  const {
    data: campaigns,
    isLoading,
    isError,
    error,
  } = useGetUpsellCampaignsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const { data: items } = useGetItemsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });

  const [createCampaign, createState] = useCreateUpsellCampaignMutation();
  const [updateCampaign, updateState] = useUpdateUpsellCampaignMutation();
  const [deleteCampaign, deleteState] = useDeleteUpsellCampaignMutation();
  const [activateCampaign, activateState] = useActivateUpsellCampaignMutation();
  const [deactivateCampaign, deactivateState] =
    useDeactivateUpsellCampaignMutation();

  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    campaignId: null,
    campaignName: "",
  });

  const campaignsList = Array.isArray(campaigns) ? campaigns : [];
  const {
    page,
    totalCount,
    pageSize,
    paginatedItems: paginatedCampaigns,
    setPage,
    setPageSize,
  } = useClientPagination(campaignsList, { pageSize: 10 });

  const itemOptions = useMemo(
    () =>
      (Array.isArray(items) ? items : []).map((item) => ({
        id: item.itemId || item.id,
        name:
          item.name ||
          item.itemName ||
          `Item ${(item.itemId || item.id) ?? ""}`,
      })),
    [items],
  );

  const itemNameById = useMemo(() => {
    const map = new Map();
    itemOptions.forEach((item) => map.set(item.id, item.name));
    return map;
  }, [itemOptions]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormError("");
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetMutationFeedback = () => {
    if (typeof createState.reset === "function") {
      createState.reset();
    }
    if (typeof updateState.reset === "function") {
      updateState.reset();
    }
  };

  const openCreateForm = () => {
    resetMutationFeedback();
    setEditingId(null);
    setForm(initialFormState);
    setFormError("");
    setShowForm(true);
  };

  const moveUpsellItem = (index, direction) => {
    setForm((prev) => {
      const next = [...prev.upsellItemIds];
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= next.length) return prev;
      [next[index], next[newIndex]] = [next[newIndex], next[index]];
      return { ...prev, upsellItemIds: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId) return;

    if (!form.name.trim()) {
      setFormError("Campaign name is required.");
      return;
    }

    if (!Array.isArray(form.triggerItemIds) || form.triggerItemIds.length === 0) {
      setFormError("Please select at least one trigger item.");
      return;
    }

    if (!Array.isArray(form.upsellItemIds) || form.upsellItemIds.length === 0) {
      setFormError("Please select at least one upsell item.");
      return;
    }

    const triggerSet = new Set(form.triggerItemIds);
    const overlap = form.upsellItemIds.find((id) => triggerSet.has(id));
    if (overlap) {
      setFormError("Trigger items and upsell items cannot overlap.");
      return;
    }

    const upsellItems = form.upsellItemIds.map((itemId, index) => ({
      itemId,
      sortOrder: index + 1,
    }));

    const payload = {
      name: form.name,
      priority: Number(form.priority) || 0,
      triggerItemIds: form.triggerItemIds,
      upsellItems,
    };

    try {
      if (editingId) {
        await updateCampaign({
          storeId: selectedStoreId,
          campaignId: editingId,
          payload: {
            name: payload.name,
            priority: payload.priority,
            triggerItemIds: payload.triggerItemIds,
            upsellItems: payload.upsellItems,
          },
        }).unwrap();
      } else {
        await createCampaign({
          storeId: selectedStoreId,
          payload: { ...payload, isActive: form.isActive },
        }).unwrap();
      }
      resetMutationFeedback();
      setEditingId(null);
      setForm(initialFormState);
      setShowForm(false);
      setFormError("");
    } catch (err) {
      // handled by UI
    }
  };

  const handleEdit = (campaign) => {
    resetMutationFeedback();
    setEditingId(getCampaignId(campaign));
    setForm({
      name: campaign.name || campaign.campaignName || "",
      priority: campaign.priority || 0,
      isActive:
        typeof campaign.isActive === "boolean"
          ? campaign.isActive
          : campaign.status === "ACTIVE",
      triggerItemIds: getTriggerItemIds(campaign),
      upsellItemIds: getOrderedUpsellItemIds(campaign),
    });
    setShowForm(true);
    setFormError("");
  };

  const handleToggleActive = async (campaign) => {
    const campaignId = getCampaignId(campaign);
    const isActive =
      typeof campaign.isActive === "boolean"
        ? campaign.isActive
        : campaign.status === "ACTIVE";
    try {
      if (isActive) {
        await deactivateCampaign({
          storeId: selectedStoreId,
          campaignId,
        }).unwrap();
      } else {
        await activateCampaign({
          storeId: selectedStoreId,
          campaignId,
        }).unwrap();
      }
    } catch (err) {
      // handled by UI
    }
  };

  const handleDelete = (campaign) => {
    setDeleteModal({
      isOpen: true,
      campaignId: getCampaignId(campaign),
      campaignName: campaign.name || campaign.campaignName || "this campaign",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteCampaign({
        storeId: selectedStoreId,
        campaignId: deleteModal.campaignId,
      }).unwrap();
    } catch (err) {
      // handled by UI
    } finally {
      setDeleteModal({
        isOpen: false,
        campaignId: null,
        campaignName: "",
      });
    }
  };

  const handleCancel = () => {
    resetMutationFeedback();
    setEditingId(null);
    setForm(initialFormState);
    setShowForm(false);
    setFormError("");
  };

  const columns = [
    { key: "name", label: "Campaign" },
    { key: "priority", label: "Priority" },
    { key: "triggerCount", label: "Trigger Items" },
    { key: "upsellCount", label: "Upsell Items" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = paginatedCampaigns.map((campaign) => {
    const id = getCampaignId(campaign);
    const isActive =
      typeof campaign.isActive === "boolean"
        ? campaign.isActive
        : campaign.status === "ACTIVE";
    const triggerItemIds = getTriggerItemIds(campaign);
    const upsellItemIds = getOrderedUpsellItemIds(campaign);

    return {
      id,
      name: campaign.name || campaign.campaignName,
      priority: campaign.priority ?? 0,
      triggerCount: triggerItemIds.length,
      upsellCount: upsellItemIds.length,
      status: (
        <div className="flex items-center gap-2">
          <ActionCheckbox
            checked={isActive}
            label={isActive ? "Active" : "Inactive"}
            onChange={() => handleToggleActive(campaign)}
          />
          <span className="text-sm text-slate-700">
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
      ),
      actions: (
        <div className="flex items-center gap-3">
          <ActionButton
            label="Edit"
            icon="edit"
            onClick={() => handleEdit(campaign)}
          />
          <ActionButton
            label="Delete"
            icon="delete"
            tone="danger"
            onClick={() => handleDelete(campaign)}
          />
        </div>
      ),
    };
  });

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Upsell Campaigns"
          subtitle="Build item-triggered offers that help guests discover relevant add-ons."
        />
        <Card>
          <Alert tone="warning">
            Please select a store to manage upsell campaigns.
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Upsell Campaigns"
        subtitle="Build item-triggered offers that help guests discover relevant add-ons."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button size="sm" onClick={openCreateForm}>
              Add Upsell Campaign
            </Button>
          )
        }
      />
      {(activateState.isError ||
        deactivateState.isError ||
        deleteState.isError) && (
        <Alert tone="error">
          {activateState.error?.data?.detail ||
            deactivateState.error?.data?.detail ||
            deleteState.error?.data?.detail ||
            "Failed to update campaign status."}
        </Alert>
      )}

      {showForm && (
        <Card>
          {formError && (
            <Alert tone="error">
              {formError}
            </Alert>
          )}
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Campaign Name"
                name="name"
                placeholder="Burger Add-ons"
                value={form.name}
                onChange={handleChange}
                required
              />
              <Input
                label="Priority"
                name="priority"
                type="number"
                value={form.priority}
                onChange={handleChange}
                helper="Higher priority is evaluated first"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <MultiSelect
                label="Trigger Items"
                options={itemOptions}
                selected={form.triggerItemIds}
                onChange={(ids) => {
                  setFormError("");
                  setForm((prev) => ({ ...prev, triggerItemIds: ids }));
                }}
                helper="Campaign appears when these items are in cart"
                searchable
                searchPlaceholder="Search trigger items..."
                maxSummaryItems={1}
                compact
              />

              <MultiSelect
                label="Upsell Items"
                options={itemOptions}
                selected={form.upsellItemIds}
                onChange={(ids) => {
                  setFormError("");
                  setForm((prev) => ({ ...prev, upsellItemIds: ids }));
                }}
                helper="Selected items can be reordered below"
                searchable
                searchPlaceholder="Search upsell items..."
                maxSummaryItems={1}
                compact
              />
            </div>

            {form.upsellItemIds.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-3 text-sm font-semibold text-slate-800">
                  Upsell Display Order
                </h3>
                <div className="space-y-2">
                  {form.upsellItemIds.map((itemId, index) => (
                    <div
                      key={itemId}
                      className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2"
                    >
                      <span className="min-w-0 truncate text-sm text-slate-800">
                        {index + 1}. {itemNameById.get(itemId) || itemId}
                      </span>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => moveUpsellItem(index, "up")}
                          disabled={index === 0}
                        >
                          Up
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => moveUpsellItem(index, "down")}
                          disabled={index === form.upsellItemIds.length - 1}
                        >
                          Down
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Checkbox
              label="Active"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
              disabled={Boolean(editingId)}
              helper={
                editingId
                  ? "To change active/inactive status, use the toggle in the campaigns list."
                  : undefined
              }
            />

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Upsell campaign {editingId ? "updated" : "created"}{" "}
                successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save upsell campaign."}
              </Alert>
            )}
            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Campaign
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500">Loading campaigns...</p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load upsell campaigns."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <>
                <Table
                  columns={columns}
                  rows={rows}
                  emptyText="No upsell campaigns yet."
                />
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  isLoading={isLoading}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </div>
        </Card>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({
            isOpen: false,
            campaignId: null,
            campaignName: "",
          })
        }
        title="Delete Upsell Campaign"
        confirmText="Delete"
        onConfirm={confirmDelete}
        isLoading={deleteState.isLoading}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.campaignName}</strong>?
        </p>
      </Modal>
    </div>
  );
};

export default UpsellCampaigns;
